import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import facturasService from '@services/facturas';
import customersService from '@services/customers';
import Menu from '@components/Menu';
import DocumentPrintModal from '@components/DocumentPrintModal';
import PDFPrint from '@components/PdfPrint';
import useDocumentPreview from '@hooks/useDocumentPreview';
import {
  MdPrint,
  MdSend,
  MdArrowBack,
  MdReceipt,
  MdPerson,
  MdPayment,
  MdAccessTime,
  MdLocalOffer,
  MdBarChart,
  MdCancel,
  MdNote,
} from "react-icons/md";
// import InvoiceTemplate from '@components/InvoiceTemplate';
import UserContext from '@components/context/userContext';

import ClipLoader from "react-spinners/ClipLoader";

import { confirmAlert } from 'react-confirm-alert';

import "./view.css";
import TestEnvironmentAlert from '@components/TestEnvironmentAlert';

const isInCurrentMonth = (epochSeconds) => {
  const inputDate = new Date(epochSeconds * 1000); // converter para ms
  const now = new Date();

  return (
    inputDate.getFullYear() === now.getFullYear() &&
    inputDate.getMonth() === now.getMonth()
  );
}

const InvoiceDetails = () => {
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { doctype, serie, doc, posto = '', tpv = '', datetime = '' } = useParams();
  const history = useHistory();
  const profile = useContext(UserContext);

  // Use the generic document preview hook
  const {
    previewUrl,
    isSubmitting,
    printerRef,
    handleSingleDocumentPrint,
    openPrintModal
  } = useDocumentPreview(profile);

  const payments = [
    { value: 1, label: "Dinheiro" },
    { value: 2, label: "Crédito" },
    { value: 3, label: "Cheque" },
    { value: 4, label: "TPA" },
    { value: 8, label: "Frota" },
    { value: 14, label: "Serviços Internos" },
    { value: 16, label: "Nota de Crédito" },
    { value: 104, label: "Transferência" },
  ];

  const handleGoBack = () => {
    if (window.history.length > 1) {
      // Existe histórico anterior — voltar
      history.goBack();
    } else {
      // Tenta fechar a janela
      window.close();

      // Se não fechar, redireciona
      setTimeout(() => {
        if (!window.closed) {
          history.push('/sales'); // Altera esta rota conforme necessário
        }
      }, 100);
    }
  };

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        // Get the invoice details directly using the document information
        const invoiceData = await facturasService.getById(profile.idEmpresa, {
          type: doctype,
          posto,
          tpv,
          serie,
          numero: doc,
          date_time: datetime
        });

        if (!invoiceData || invoiceData === 'NOK' || Object.keys(invoiceData) === 0) {
          throw new Error('Failed to find invoice');
        }

        invoiceData.idcustomer = invoiceData.cliente;
        invoiceData.idposto = invoiceData.armazem;
        invoiceData.anulado = invoiceData.anulado === 'True';
        invoiceData.payment = invoiceData['cod_forma_pag'] ? parseInt(invoiceData['cod_forma_pag']) : 2;
        invoiceData.payment_method = payments.find(p => p.value === invoiceData.payment);

        // Fetch customer details
        if (invoiceData.cliente) {
          const customerData = await customersService.getById(profile.idEmpresa, invoiceData.cliente);
          if (customerData && customerData.length > 0) {
            setCustomer(customerData[0]);
          }
        }

        setInvoice(invoiceData);
      } catch (err) {
        console.error('Error fetching invoice details:', err);
        setError(err.message || 'Error fetching invoice details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.idEmpresa, doctype, serie, doc]);

  const handlePrint = async ({ selectedPageSize, selectedPageType }) => {
    await handleSingleDocumentPrint(
      {
        ...invoice,
        date_time: datetime
      },
      selectedPageSize,
      selectedPageType,
      {
        endpoint: 'facturas'
      }
    );
  };

  const handleEmail = async () => {
    let email = prompt("Email:", invoice.email || customer.email);
    
    if (email) {
      setIsSubmiting(true);
      //post
      const response = await facturasService.email({
        idempresa: profile.idEmpresa,
        type: invoice.type,
        posto: invoice.posto || null,
        tpv: invoice.tpv || null,
        date_time: invoice.date_time,
        serie: invoice.serie,
        numero: invoice.numero,
        email: email
      });
      if (response === 'OK') {
        setIsSubmiting(false);
        alert('Email enviado com sucesso');
      } else {
        alert('Erro ao enviar email');
      }
    }
  };

  const handleCancel = async () => {
    setIsSubmiting(true);
    confirmAlert({
        title: 'Confirmação',
        message: `Tem a certeza que deseja anular o documento ${invoice.doc}?\nEsta ação não pode ser desfeita.`,
        buttons: [
            {
                label: 'Sim',
                onClick: () => {
                    facturasService.unset(profile.idEmpresa, {
                      type: invoice.type,
                      posto: invoice.posto || '',
                      tpv: invoice.tpv || '',
                      serie: invoice.serie,
                      numero: invoice.numero,
                      date_time: datetime
                    })
                    .then(response => {
                        setIsSubmiting(false);
                        if (response !== 'NOK') {
                            window.location.reload();
                        }
                    })
                    .catch(error => {
                        setIsSubmiting(false);
                        console.error(error);
                    });
                }
            },
            {
                label: 'Não',
                onClick: () => {
                  setIsSubmiting(false);
                }
            }
        ]
    });
  };

  if (loading) {
    return (
      <div>
        <Menu />
        <div className="container">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Menu />
        <div className="container">
          <div className="alert alert-danger">{error}</div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div>
        <Menu />
        <div className="container">
          <div className="alert alert-warning">Recibo não encontrado</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Menu />
      <div className="fullContainer" style={{ maxWidth: '100%' }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: 'left',
              marginTop: '75px',
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li>
              <a href="/sales">Faturação</a>
            </li>
            <li className="active">
              <span>Detalhes do Recibo</span>
            </li>
          </ol>
        </div>
      </div>
      <TestEnvironmentAlert />

      <div className="invoice-container">
      {/* Header Bar */}
      <div className="header-bar" style={{ backgroundColor: invoice.anulado ? '#910000' : '#262626' }}>
        <div className="header-content">
          <button className="btn-back" onClick={handleGoBack}>
            <MdArrowBack size={20} /> <span>Voltar</span>
          </button>
          <div className="document-info">
            <MdReceipt size={24} />
            <div>
              <h1>{invoice.doc} {invoice.anulado ? '(anulado)' : ''}</h1>
              <p>
                {new Date(invoice.date_time * 1000).toLocaleString('pt-PT', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          <div className="header-actions">
            {invoice.anulado ? null : <button disabled={!isInCurrentMonth(invoice.date_time)} className="btn-danger-action" onClick={handleCancel}>
              { isSubmiting ? <ClipLoader size={15} /> : <MdCancel size={18} />} <span>Anular</span>
            </button>}
            <button className="btn-action" onClick={handleEmail}>
              { isSubmiting ? <ClipLoader size={15} /> : <MdSend size={18} />} <span>Enviar Email</span>
            </button>
            <button className="btn-action" onClick={() => openPrintModal()}>
            { isSubmitting ? <ClipLoader size={15} /> : <MdPrint size={18} />} <span>Imprimir</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-wrapper">
        {/* Main Content */}
        <div className="invoice-content">
          {/* Client and Document Info */}
          <div className="info-cards-container">
            <div className="info-card client-card">
              <div className="card-header">
                <MdPerson size={20} />
                <h2>Informação do Cliente</h2>
              </div>
              <div className="card-content">
                <h3>{customer?.name || 'Consumidor Final'}</h3>
                {invoice?.nif !== 999999990 ? (
                  <div className="detail-item">
                    <span>NIF: {invoice?.nif}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="info-card payment-card">
              <div className="card-header">
                <MdPayment size={20} />
                <h2>Detalhes de Pagamento</h2>
              </div>
              <div className="card-content">
                <div className="detail-item">
                  <MdAccessTime size={16} />
                  <span>
                    Pago: 
                    {invoice.data_vencimento ? invoice?.data_vencimento || '' : new Date(invoice.date_time * 1000).toLocaleString('pt-PT', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    }).slice(0, 10)}
                  </span>
                </div>
                <div className="detail-item">
                  <MdLocalOffer size={16} />
                  <span>Método: {invoice.forma_pagamento}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Document Observations */}
          {invoice.obs && (
            <div className="observations-section">
              <div className="section-header">
                <MdNote size={24} />
                <h2>Observações do Documento</h2>
              </div>
              <div className="observations-content">
                <p>{invoice.obs}</p>
              </div>
            </div>
          )}

          {/* Products Table */}
          <div className="products-section">
            <div className="section-header">
              <MdBarChart size={24} />
              <h2>Linhas</h2>
            </div>
            <div className="table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Nº Docuemnto</th>
                    <th>Data Emissão</th>
                    <th className="text-center">valor Inicial</th>
                    <th className="text-end">valor Liquidado</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.doc}</td>
                      <td>{line.data_documento}</td>
                      <td className="text-end">{parseFloat(line.valor_inicial)?.toFixed(2)} €</td>
                      <td className="text-end">{parseFloat(line.valor_liquido)?.toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="totals-section">
            <div className="totals-card">
              {/* <div className="totals-row">
                <span>Subtotal</span>
                <span>{(invoice?.lines || []).reduce((subtotal, line) => subtotal + parseFloat(line['valor_liquido']), 0).toFixed(2)} €</span>
              </div>
              <div className="totals-row">
                <span>Descontos</span>
                <span>{parseFloat(invoice.valor_desconto)?.toFixed(2)} €</span>
              </div> */}
              <div className="totals-row total" style={{ border: 0, margin: 0, padding: 0 }}>
                <span>Total</span>
                <span>{parseFloat(invoice.valor_liquido)?.toFixed(2)} €</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PDFPrint ref={printerRef} pdfUrl={previewUrl} />
    <DocumentPrintModal
      onPrint={handlePrint}
      isSubmitting={isSubmitting}
    />
  </div>
  );
};

export default InvoiceDetails;